import { Component, OnInit } from '@angular/core';
import { ToastService } from '../../../core/services/toast.service';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { UntypedFormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { CommunicationTemplate } from '../../../core/models/entities/communication-template';
import { communicationTemplatesActions } from 'angular/app/core/store/communication-templates';
import { ModalService } from '../../../core/services/modal.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { CommunicationTemplatesService } from '../../../core/services/communication-templates.service';
import { ApiError } from '../../../core/models/common/api-error';

@Component({
  selector: 'app-communication-templates-page',
  template: `
    <!-- Page Header -->
    <app-page-header title="Modèles de communication">
      <ng-template #actions>
        <app-button (click)="creatingCommunicationTemplate = true"
          >Ajouter</app-button
          >
        </ng-template>
      </app-page-header>
    
      <app-spacer size="6"></app-spacer>
    
      @if (loading$ | async) {
        <app-loader [centered]="true"></app-loader>
      } @else {
        <app-fieldset>
          <div title>Modèles de communication</div>
          <div description
            >Créez ici des modèles de communication réutilisables à envoyer avec
            une confirmation/annulation de réservation.</div
            >
    
            <div>
              @if (
                !creatingCommunicationTemplate &&
                (communicationTemplates$ | async)?.length === 0
                ) {
                <app-empty-state
                  icon="books"
                  message="Vous n'avez pas encore créé de modèle de communication."
                  [inline]="true"
                  [cardStyle]="true"
                  (buttonClicked)="creatingCommunicationTemplate = true"
                ></app-empty-state>
              }
    
              <div class="space-y-6" data-cy="communication-templates-list">
                @if (creatingCommunicationTemplate) {
                  <div>
                    <app-list-item>
                      <app-communication-template-form
                        edit
                        (onCancel)="creatingCommunicationTemplate = false"
                  (communicationTemplateSaved)="
                    creatingCommunicationTemplate = false
                  "
                      ></app-communication-template-form>
                    </app-list-item>
                  </div>
                }
    
                @for (
                  communicationTemplate of communicationTemplates$ | async; track
                  communicationTemplate; let i = $index) {
                  <div
                    >
                    <app-list-item [id]="i">
                      <div toggle><i class="fa fal fa-books"></i></div>
                      <div title class="font-medium">{{
                        communicationTemplate.name
                      }}</div>
                      @if (
                        editingCommunicationTemplate?.id !==
                        communicationTemplate.id
                        ) {
                        <div
                          actions
                          class="space-x-4"
                          >
                          <app-icon-action
                            icon="pen"
                            [attr.data-cy]="'edit'"
                    (click)="
                      editingCommunicationTemplate = communicationTemplate
                    "
                          ></app-icon-action>
                          <app-icon-action
                            icon="trash"
                            [attr.data-cy]="'delete'"
                    (click)="
                      deleteCommunicationTemplate(communicationTemplate.id)
                    "
                          ></app-icon-action>
                        </div>
                      }
                      @if (
                        editingCommunicationTemplate?.id ===
                        communicationTemplate.id
                        ) {
                        <app-communication-template-form
                          [communicationTemplate]="communicationTemplate"
                          (onCancel)="editingCommunicationTemplate = null"
                  (communicationTemplateSaved)="
                    editingCommunicationTemplate = null
                  "
                          edit
                        ></app-communication-template-form>
                      }
                    </app-list-item>
                  </div>
                }
              </div>
            </div>
          </app-fieldset>
        }
    `,
  standalone: false,
})
export class CommunicationTemplatesPageComponent
  extends DestroyableComponent
  implements OnInit
{
  form: UntypedFormGroup;
  sendingData = false;
  creatingCommunicationTemplate = false;
  editingCommunicationTemplate: CommunicationTemplate;
  loading$: Observable<boolean> = this.store.select(
    fromStore.selectCommunicationTemplatesLoading,
  );
  communicationTemplates$: Observable<CommunicationTemplate[]> =
    this.store.select(fromStore.selectAllCommunicationTemplates);

  constructor(
    private toastService: ToastService,
    private store: Store,
    private modalService: ModalService,
    private communicationTemplatesService: CommunicationTemplatesService,
  ) {
    super();
  }

  async ngOnInit() {
    this.store.dispatch(communicationTemplatesActions.findAll());
  }

  deleteCommunicationTemplate(id: number) {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Supprimer modèle de communication',
      message:
        'Êtes-vous sûr·e de vouloir supprimer le modèle de communication ?',
      okAction: {
        title: 'Supprimer',
        variant: 'danger',
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        await this.communicationTemplatesService.delete(id);
        this.store.dispatch(communicationTemplatesActions.removeOne({ id }));
        this.toastService.success('Modèle de communication supprimé.');
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      }
      instance.saving = false;
      this.modalService.close();
    });
  }
}
